import { cloneElement, isValidElement, useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * A small menu button (WAI-ARIA APG "menu button" pattern).
 *
 * Hand-rolled rather than pulled from Radix because this is the first and only
 * menu in the app — decisions.md 15 sanctions taking Radix components a la carte
 * when they are actually needed, and one menu does not justify the dependency.
 * The behaviour it has to get right is short and testable (W-21):
 *
 *   - the trigger reports aria-expanded and owns the menu via aria-controls
 *   - Enter / Space / ArrowDown on the trigger open on the first item, ArrowUp on the last;
 *     a click opens on the first item too
 *   - inside: ArrowDown / ArrowUp move (wrapping), Home / End jump; roving tabindex
 *   - Escape closes and returns focus to the trigger; Tab closes and moves on
 *   - a click outside closes
 *   - the menu is a real list of menuitems (each MenuItem's child gets role="menuitem")
 *
 * If a second menu, a dialog or a combobox appears, replace this with
 * @radix-ui/react-dropdown-menu rather than growing it.
 */

type MenuProps = {
  label: string;
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "start" | "end";
  className?: string;
  triggerClassName?: string;
  "data-testid"?: string;
};

type FocusTarget = "first" | "last";

export function Menu({
  label,
  trigger,
  children,
  align = "end",
  className,
  triggerClassName,
  ...props
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState<FocusTarget>("first");
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const items = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  function openAt(target: FocusTarget) {
    setFocusTarget(target);
    setOpen(true);
  }

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    const list = items();
    (focusTarget === "last" ? list[list.length - 1] : list[0])?.focus();
  }, [open, focusTarget]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      close(true);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function onTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openAt("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openAt("last");
    }
  }

  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const list = items();
    if (list.length === 0) return;
    const current = list.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    if (event.key === "ArrowDown") next = current < 0 ? 0 : (current + 1) % list.length;
    else if (event.key === "ArrowUp")
      next = current < 0 ? list.length - 1 : (current - 1 + list.length) % list.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = list.length - 1;
    else if (event.key === "Tab") {
      // Leave the menu the way the user is going; focus moves on by itself.
      setOpen(false);
      return;
    }
    if (next === null) return;
    event.preventDefault();
    list[next]?.focus();
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        onClick={() => (open ? close(false) : openAt("first"))}
        onKeyDown={onTriggerKeyDown}
        className={triggerClassName}
        {...props}
      >
        {trigger}
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={id}
          role="menu"
          aria-label={label}
          className={cn(
            "absolute top-full z-50 mt-2 min-w-48 overflow-hidden rounded-lg border",
            "border-[var(--border)] bg-[var(--surface-1)] py-1 shadow-lg",
            align === "end" ? "right-0" : "left-0",
          )}
          onKeyDown={onMenuKeyDown}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One row inside a Menu. Its single child (a link or a button) becomes the `menuitem`, out of the
 * Tab order (the menu moves focus with the arrow keys).
 */
export function MenuItem({
  children,
  className,
}: {
  children: React.ReactElement<{ role?: string; tabIndex?: number }>;
  className?: string;
}) {
  return (
    <div role="none" className={cn("px-1", className)}>
      {isValidElement(children)
        ? cloneElement(children, { role: "menuitem", tabIndex: -1 })
        : children}
    </div>
  );
}

export const menuItemClasses = cn(
  "block w-full rounded-md px-3 py-2 text-left text-sm",
  "text-[var(--text-primary)] hover:bg-[var(--surface-3)]",
  "focus-visible:bg-[var(--surface-3)] focus-visible:outline-none",
);
